// dashboard/components/MobileDrawer.jsx
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Typography,
  Divider,
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  Menu as MenuIcon,
  Logout as LogoutIcon,
  School as SchoolIcon,
  LibraryBooks as LibraryBooksIcon,
  Telegram as TelegramIcon,
  Subscriptions as SubscriptionsIcon,
  AccountBalanceWallet as WalletIcon,
  Receipt as ReceiptIcon,
  Notifications as NotificationsIcon,
  Person as PersonIcon,
  Support as SupportIcon,
  Security,
  Settings,
  Payment,
  TrendingUp,
  Redeem,
  People,
  Email,
  Help,
  CreditCard,
  Storefront,
  Store,
} from "@mui/icons-material";
import { useState } from "react";
import { useAuth } from "../../context/authContext";
import { useNavigate } from "react-router-dom";
import { BrandLogo } from '../../branding/BrandAsset';

const MobileDrawer = ({ active, setActive }) => {
  const [open, setOpen] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const { logout, user } = useAuth(); // Get user here at component level
  const navigate = useNavigate();

  const navItems = [
    {
      label: "Main",
      items: [
        { text: "Overview", icon: <DashboardIcon />, id: "Overview" },
        { text: "My Access", icon: <Security />, id: "MyAccess" },
        { text: "Notifications", icon: <NotificationsIcon />, id: "Notifications" },
      ],
    },
    {
      label: "Academy",
      items: [
        { text: "All Courses", icon: <LibraryBooksIcon />, id: "AllCourses" },
        { text: "My Courses", icon: <SchoolIcon />, id: "MyCourses" },
        { text: "Learning Progress", icon: <TrendingUp />, id: "LearningProgress" },
      ],
    },
    {
      label: "Telegram Signals",
      items: [
        { text: "Signal Channel", icon: <TelegramIcon />, id: "TelegramSignals" },
        { text: "Subscription Plans", icon: <SubscriptionsIcon />, id: "SubscriptionPlans" },
        { text: "My Subscription", icon: <CreditCard />, id: "MySubscription" },
        { text: "Renew / Extend", icon: <Payment />, id: "RenewSubscription" },
      ],
    },
    {
      label: "Wallet & Payments",
      items: [
        { text: "My Wallet", icon: <WalletIcon />, id: "Wallet" },
        { text: "Deposit Funds", icon: <Payment />, id: "DepositFunds" },
        { text: "Transaction History", icon: <ReceiptIcon />, id: "Transactions" },
        { text: "Payment History", icon: <ReceiptIcon />, id: "PaymentHistory" },
      ],
    },
    {
      label: "Marketplace",
      items: [
        { text: "Forex Tools", icon: <Storefront />, id: "ForexTools" },
        { text: "My Tools / Licenses", icon: <Store />, id: "MyTools" },
      ],
    },
    {
      label: "Rewards",
      items: [
        { text: "Referral Program", icon: <People />, id: "ReferralProgram" },
        { text: "My Referral Earnings", icon: <Redeem />, id: "ReferralEarnings" },
      ],
    },
    {
      label: "Support",
      items: [
        { text: "Help & Support", icon: <Help />, id: "HelpSupport" },
        { text: "Contact Support", icon: <Email />, id: "ContactSupport" },
      ],
    },
    {
      label: "Account",
      items: [
        { text: "My Profile", icon: <PersonIcon />, id: "Profile" },
        { text: "Security", icon: <Security />, id: "Security" },
        { text: "Settings", icon: <Settings />, id: "Settings" },
      ],
    },
  ];

  const handleItemClick = (item) => {
    if (item.isLogout) {
      setLogoutDialogOpen(true);
    } else {
      setActive(item.id);
      setOpen(false);
    }
  };

  const handleLogoutConfirm = async () => {
    setLogoutDialogOpen(false);
    setOpen(false);
    await logout();
    navigate('/login');
  };

  const handleLogoutCancel = () => {
    setLogoutDialogOpen(false);
  };

  // Helper function to get user initials - using user from component scope
  const getUserInitials = () => {
    if (!user) return 'U';
    if (user.first_name && user.last_name) {
      return `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`.toUpperCase();
    }
    if (user.first_name) return user.first_name.charAt(0).toUpperCase();
    return 'U';
  };

  return (
    <>
      <IconButton
        onClick={() => setOpen(true)}
        sx={{ position: "fixed", top: 16, left: 16, zIndex: 1300, color: "#001f3f" }}
      >
        <MenuIcon sx={{ fontSize: 28 }} />
      </IconButton>

      <Drawer
        anchor="left"
        open={open}
        onClose={() => setOpen(false)}
        sx={{
          "& .MuiDrawer-paper": {
            width: 280,
            backgroundColor: "#001f3f",
            color: "#fff",
          },
        }}
      >
        {/* Header */}
        <Box sx={{ p: 2, borderBottom: '1px solid rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <BrandLogo surface="dark" alt="ProTradesInsight" style={{ width: 174, maxHeight: 52, objectFit: 'contain' }} />
          <IconButton onClick={() => setOpen(false)} sx={{ color: '#fff' }}>
            <MenuIcon />
          </IconButton>
        </Box>

        {/* User Profile */}
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              bgcolor: '#2ecc71',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontWeight: 700,
              fontSize: '14px',
            }}
          >
            {getUserInitials()}
          </Box>
          <Box sx={{ ml: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#fff' }}>
              {user?.first_name || 'User'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
              {user?.email || ''}
            </Typography>
          </Box>
        </Box>

        {/* Navigation */}
        <Box sx={{ flex: 1, overflowY: 'auto', px: 1, py: 1 }}>
          {navItems.map((section, sectionIndex) => (
            <Box key={sectionIndex}>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  px: 1.5,
                  pt: 1.5,
                  pb: 0.5,
                  color: 'rgba(255,255,255,0.4)',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.8px',
                  fontSize: '0.6rem',
                }}
              >
                {section.label}
              </Typography>
              <List sx={{ px: 0 }}>
                {section.items.map((item) => {
                  const isActive = active === item.id;

                  return (
                    <ListItem
                      button
                      key={item.text}
                      onClick={() => handleItemClick(item)}
                      sx={{
                        mb: 0.3,
                        mx: "6px",
                        borderRadius: "10px",
                        py: 0.8,
                        backgroundColor: isActive ? "rgba(46, 204, 113, 0.15)" : "transparent",
                        borderLeft: isActive ? "3px solid #2ecc71" : "3px solid transparent",
                        "&:hover": { 
                          backgroundColor: "rgba(255,255,255,0.08)" 
                        },
                      }}
                    >
                      <ListItemIcon 
                        sx={{ 
                          color: isActive ? "#2ecc71" : "rgba(255,255,255,0.6)",
                          minWidth: 36,
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography 
                            sx={{ 
                              color: isActive ? "#ffffff" : "rgba(255,255,255,0.7)",
                              fontWeight: isActive ? 600 : 400,
                              fontSize: '0.85rem',
                            }}
                          >
                            {item.text}
                          </Typography>
                        }
                      />
                    </ListItem>
                  );
                })}
              </List>
              {sectionIndex < navItems.length - 1 && (
                <Divider sx={{ mx: 2, my: 0.5, backgroundColor: 'rgba(255,255,255,0.05)' }} />
              )}
            </Box>
          ))}
        </Box>

        {/* Logout */}
        <Box sx={{ p: 1.5, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
          <ListItem
            button
            onClick={() => setLogoutDialogOpen(true)}
            sx={{
              borderRadius: "10px",
              mx: "6px",
              backgroundColor: "rgba(231, 76, 60, 0.1)",
              "&:hover": {
                backgroundColor: "rgba(231, 76, 60, 0.2)",
              },
            }}
          >
            <ListItemIcon sx={{ color: "#e74c3c", minWidth: 36 }}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography sx={{ color: "#e74c3c", fontSize: '0.85rem', fontWeight: 500 }}>
                  Logout
                </Typography>
              }
            />
          </ListItem>
        </Box>
      </Drawer>

      {/* Logout Confirmation Dialog */}
      <Dialog
        open={logoutDialogOpen}
        onClose={handleLogoutCancel}
        aria-labelledby="mobile-logout-dialog-title"
      >
        <DialogTitle id="mobile-logout-dialog-title" sx={{ color: '#d32f2f' }}>
          Confirm Logout
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to logout? You will be redirected to the login page.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLogoutCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleLogoutConfirm} color="error" variant="contained">
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MobileDrawer;
